import { getSdk } from "./sdkService";
import { config } from "../config/config";
import { checkEpisodeData, getSeasonEpisodeFromMediaGuid } from "./titleDataService";
import { getProgressData } from "./mediaListService";
import { getAppState } from "../state";
import { getEpisodeLabel } from "./copyService";
import padStart from "../utils/padStart";
import { i18n } from "./i18nService";
import { getFirstFreeEpisodeIndex, findEpisodeIndex, isFVODFreeToAir } from "../utils/fvodUtils";
import { DPLAY } from "../utils/epgUtil";
import { isLowEndLG } from "../utils/checkLgNewModel";
import { getContentTypeForAnalytics } from "./byteEventService";
import { STARZ } from "../utils/mediaDataRetrieval";

const HAS_4K = "4kProgramId";
const STREAMING_URL = "streamingUrl";
const RELEASE_PID = "releasePid";
const RELEASE_URL = "releaseUrl";
const DASH_WIDEVINE_ADS = "dash_widevine_ads";
const DASH_WIDEVINE_FP_ADS = "dash_widevine_fp_ads";
const DASH_WIDEVINE_TK_ADS = "dash_widevine_tk_ads";
const DURATION = "duration";
const TITLE = "title";
const SEASON_NUMBER = "tvSeasonNumber";
const EPISODE_NUMBER = "tvSeasonEpisodeNumber";
const SHORT_DESCRIPTION = "shortDescription";
const ID = "id";
const RENT = "rent";
const INIT = "init";
const ACTIVE = "active";
const PURCHASE = "purchase";
const EXPIRED = "expired";

/**
 * Gets episode media content either from last watched episode (in case there is one) or
 * the first of the episodes list. Needs a React component class context since
 * this method is making use of inner context properties.
 * @returns {Object} - Episode media content.
 * @public
 */
async function getEpisodeMediaContent(is4K) {
  const lastSeenEpisode = is4K ? await getEpisode4KData.call(this) : this.lastSeenEpisode;
  const episode = lastSeenEpisode || this.episodesList[this.firstFreeEpisodeIndex] || this.episodesList[0];
  return episode.mediaContent[0].content;
}

/**
 * Gets episode video data properties from last seen episode in case it exists (from watchlist) or
 * from the first episode available in the episodes list. Needs a React component class context since
 * this method is making use of inner context properties.
 * @param {String} property - Property to be found within title video content object.
 * @param {Boolean} is4K - Condition that determines if content is suitable for 4K playback.
 * @returns {String|Array} - Value of the received property to be found within video content object.
 * @public
 */
async function getEpisodeVideoData(property, is4K) {
  const episodeMedia = await getEpisodeMediaContent.call(this, is4K);
  return episodeMedia.video[property];
}

async function getEpisodeVideoDataWidevine(property, is4K, noDrm = null) {
  const episodeMedia = await getEpisodeMediaContent.call(this, is4K);
  if (noDrm?.dPlay === DPLAY.free) {
    return episodeMedia.video.dash_widevine_fp[property];
  } else if (noDrm?.dPlay === DPLAY.token) {
    return episodeMedia.video.dash_widevine_tk[property];
  } else {
    return episodeMedia.video.dash_widevine[property];
  }
}

/**
 * Returns external subtitles from last seen episode in case it exists (from watchlist) or
 * from the first episode available in the episodes list. Needs a React component class context since
 * this method is making use of inner context properties.
 * @param {Boolean} is4K - Condition that determines if content is suitable for 4K playback.
 * @returns {Object} - Object containing the languages available subtitles with their URLs to make
 * the request to retrieve them.
 * @public
 */
async function getEpisodeExternalSubtitles(is4K) {
  const episodeMedia = await getEpisodeMediaContent.call(this, is4K);
  return episodeMedia.subtitles;
}

/** returns ads country right */
export async function getAdsCountryRightsData(seasonNumber = 1, episodeNumber = 1) {
  const episodeIndex = this.episodesList.findIndex(
    title => episodeNumber === title.tvSeasonEpisodeNumber && seasonNumber === title.tvSeasonNumber
  );
  return this.episodesList[episodeIndex].adsCountryRights;
}

/**
 * Gets episode filmstrip data from last seen episode in case it exists (from watchlist) or
 * from the first episode available in the episodes list. Needs a React component class context since
 * this method is making use of inner context variables.
 * @param {Boolean} is4K - Condition that determines if content is suitable for 4K playback.
 * @returns {String} - String containing the URL to make the request to retrieve filmstrips.
 * @private
 */
async function getEpisodeFilmstripData(is4K) {
  const episode = is4K
    ? await getEpisode4KData.call(this)
    : this.lastSeenEpisode
    ? this.lastSeenEpisode
    : this.episodesList[this.firstFreeEpisodeIndex]
    ? this.episodesList[this.firstFreeEpisodeIndex]
    : this.episodesList[0];
  return episode.mediaContent[0].content.filmstrip;
}

/**
 * Gets episode title 4K related content either from last watched episode (in case there is one) or
 * the first of the episodes list that contain it. Needs a React component class context since
 * this method is making use of inner context properties.
 * @returns {Object} - Episode title 4K related content.
 * @public
 */
async function getEpisode4KData() {
  const sdk = await getSdk();
  const episode = this.episodesList[this.firstFreeEpisodeIndex][HAS_4K]
    ? this.episodesList[this.firstFreeEpisodeIndex][HAS_4K]
    : this.episodesList[0][HAS_4K];
  return this.lastSeenEpisode && this.lastSeenEpisode.title4K
    ? this.lastSeenEpisode.title4K
    : await sdk.mediaManager.getTitle(episode, { is4K: true });
}

/**
 * Gets trailer media related data to be displayed on player. Needs a React component class context since
 * this method is making use of this.state.
 * @returns {Object} - Trailer data object to be displayed on the player.
 * @public
 */
export function getTrailerVideoData() {
  const playbackAnalytics = config.get().featureToggles.playbackAnalytics;
  return {
    videoType: "trailer",
    contentTypeYoubora: "trailer",
    manifest: this.state.trailer,
    title: this.state.title,
    id: this.state.id,
    duration: playbackAnalytics && playbackAnalytics.trailerFixedDuration // This is due to a limitation in Youbora. In order to avoid an extra call to endpoint "init" from youbora we have to send duration
  };
}

export function buyAssetDetails(assetType, assetData) {
  const { title, id, titleData } = this.state;
  const { posterLandscapeImageForBuyAsset } = this.props.applicationState;
  const { contentId, contentTitle, oldPrice, newPriceId, newPrice, newPriceCurrency } = assetData;
  return {
    data: {
      contentTitle: contentTitle ? contentTitle : title,
      contentId: contentId ? contentId : id,
      contentOldPrice: oldPrice && oldPrice,
      contentNewPriceId: newPriceId,
      contentPrice: newPrice,
      contentCurrency: newPriceCurrency,
      contentImage: titleData?.buyAssetBackgroundImage
        ? titleData?.buyAssetBackgroundImage
        : posterLandscapeImageForBuyAsset,
      contentTagList: titleData ? titleData.tagList : "",
      contentTypeShow: i18n.get(`pages.detail.${assetType}`),
      contentType: assetType
    }
  };
}

export function getExpireTagTime(title, status) {
  const CurrentDate = new Date();
  const endTime = new Date(status[0]?.endDate.replace(/ /, "T") + "Z");
  const parseDate = Date.parse(endTime);
  const dateObj = new Date(parseDate);
  const diff = dateObj.getTime() - CurrentDate.getTime();

  if (diff > 0) {
    if (
      status &&
      status.length > 0 &&
      status[0]?.subscriptionType?.toLowerCase() === RENT &&
      (status[0]?.status?.toLowerCase() === ACTIVE || status[0]?.status.toLowerCase() === INIT)
    ) {
      const hours = Math.floor(diff / (1000 * 3600));
      const minutes = Math.floor((diff % (1000 * 3600)) / (1000 * 60));
      const days = Math.floor(hours / 24);
      const remHours = Math.floor(hours % 24);
      if (hours < 48 && days < 2) {
        if (hours >= 0 && hours === 1) {
          title.remTime = `${i18n.get("global.tagLabels.expiryhour", hours, minutes)}`;
        } else if (hours === 2) {
          title.remTime = `${i18n.get("global.tagLabels.expiryTwohours", hours, minutes)}`;
        } else if (hours >= 3 && hours <= 10) {
          title.remTime = `${i18n.get("global.tagLabels.expiryThreehours", hours, minutes)}`;
        } else if (hours > 11 || hours === 11 || minutes > 0) {
          title.remTime = `${i18n.get("global.tagLabels.expiryTenhours", hours, minutes)}`;
        }
      } else if (days > 2 || days === 2) {
        if (days === 1) {
          title.remTime = `${i18n.get("global.tagLabels.expiryDay", days, remHours)}`;
        } else if (days === 2) {
          title.remTime = `${i18n.get("global.tagLabels.expiryTwoDay", days, remHours)}`;
        } else if (days >= 3 && days <= 10) {
          title.remTime = `${i18n.get("global.tagLabels.expiryThreeDay", days, remHours)}`;
        } else if (days >= 11 || days === 11) {
          title.remTime = `${i18n.get("global.tagLabels.expiryTenDay", days, remHours)}`;
        }
      } else {
        title.remTime = `${i18n.get("global.tagLabels.expiryMints", minutes)}`;
      }
    } else if (
      status &&
      status.length > 0 &&
      status[0]?.subscriptionType?.toLowerCase() === PURCHASE &&
      (status[0].status?.toLowerCase() === INIT || status[0].status?.toLowerCase() === ACTIVE)
    ) {
      title.remTime = `${i18n.get("global.tagLabels.buy")}`;
    }
  } else {
    title.remTime = ``;
  }
  return title;
}

export function getYouboraPrams(title, status) {
  if (status && status.length > 0 && status[0]?.subscriptionType === "RENT") {
    title.contentType = "RENT";
  } else if (status && status.length > 0 && status[0].subscriptionType === "PURCHASE") {
    title.contentType = "BUY";
  } else {
    title.contentType = "SVOD";
  }
  return title;
}

function getExpireDate(title) {
  if (title && title.mediaContent.length > 0 && title.mediaContent.length > 0 && title.mediaContent[0].expirationDate) {
    const currentTimestamp = new Date().getTime();
    const unixTime = Math.floor(currentTimestamp / 1000);
    const tsExpire = title.mediaContent[0].expirationDate / 1000;
    let exTime = tsExpire - unixTime;
    let diffSecond = Math.floor(exTime / 1000);
    let minutes = Math.floor((exTime / 60) % 60);
    let hours = Math.floor((exTime / 3600) % 24);
    let days = Math.floor(exTime / (24 * 3600));
    let remHours = Math.floor(hours % 24);
    if (days <= 14) {
      if (hours < 48 && days < 2) {
        if (hours >= 0 && hours === 1) {
          title.remTime = `${i18n.get("global.tagLabels.lastChance.expiryhour", hours, minutes)}`;
        } else if (hours === 2) {
          title.remTime = `${i18n.get("global.tagLabels.lastChance.expiryTwohours", hours, minutes)}`;
        } else if (hours >= 3 && hours <= 10) {
          title.remTime = `${i18n.get("global.tagLabels.lastChance.expiryThreehours", hours, minutes)}`;
        } else if (hours > 11 || hours === 11 || minutes > 0) {
          title.remTime = `${i18n.get("global.tagLabels.lastChance.expiryTenhours", hours, minutes)}`;
        }
      } else if (days > 2 || days === 2) {
        if (days === 1) {
          title.remTime = `${i18n.get("global.tagLabels.lastChance.expiryDay", days, remHours)}`;
        } else if (days === 2) {
          title.remTime = `${i18n.get("global.tagLabels.lastChance.expiryTwoDay", days, remHours)}`;
        } else if (days >= 3 && days <= 10) {
          title.remTime = `${i18n.get("global.tagLabels.lastChance.expiryThreeDay", days, remHours)}`;
        } else if (days >= 11 || days === 11) {
          title.remTime = `${i18n.get("global.tagLabels.lastChance.expiryTenDay", days, remHours)}`;
        }
      } else if (minutes > 0) {
        title.remTime = `${i18n.get("global.tagLabels.lastChance.expiryMints", minutes)}`;
      } else {
        title.remTime = ``;
      }
    } else {
      title.remTime = ``;
    }
    return title;
  }
}

export function expirationDate(title, status) {
  if (status && status.length > 0 && status[0]?.status.toLowerCase() === EXPIRED) {
    return getExpireDate(title);
  } else if (status && status.length === 0) {
    return getExpireDate(title);
  }
}
/**
 * Gets the video content related data to pass it to the player. Needs a React
 * component class context since this method is making use of inner context properties.
 * @param {Boolean} is4K - Condition that determines if content is suitable for 4K playback.
 * @returns {Object} Object to be passed to player containing all the necessary data to be displayed or
 * to reproduce the content.
 * @public
 */
export async function getVideoDataForPlayer(is4K) {
  this.firstFreeEpisodeIndex = getFirstFreeEpisodeIndex(this.episodesList, this.freeEpisodes);
  const currentHas4K =
    is4K && // Requested 4K play AND
    ((!this.isSeries && this.mediaData.media4K) || // Is Movie and has media4K OR
      !!(
        (this.lastSeenEpisode && this.lastSeenEpisode[HAS_4K]) ||
        this.episodesList[this.firstFreeEpisodeIndex][HAS_4K] ||
        this.episodesList[0][HAS_4K]
      ));
  const movieVideo = currentHas4K ? this.mediaData.media4K.content.video : this.mediaData.mediaHD.content.video;
  const episode =
    this.isSeries &&
    (this.episodeTitle ||
      this.lastSeenEpisode ||
      this.episodesList[this.firstFreeEpisodeIndex] ||
      this.episodesList[0]);

  const noDrmConfig = this.isSeries ? episode?.config : this.state.titleData?.config;
  const profileType = getAppState().currentProfileType;
  const getModelDevice = await getSdk().systemManager.device.getRealModel();
  const streamingData = getStreamingURL(
    this.state.adsCountryRights,
    this.isSeries ? episode?.ecommerceAds : this.state.titleData.ecommerceAds,
    noDrmConfig,
    movieVideo
  );
  const contentOwnership =
    this.state.titleData?.contentOwnership && this.state.titleData?.contentOwnership.length > 0
      ? this.state.titleData?.contentOwnership[0]
      : STARZ;
  return !this.isSeries
    ? {
        videoType: "movie",
        contentTypeYoubora: getContentTypeForAnalytics(this.state),
        manifest: streamingData?.streamingUrl || movieVideo.streamingUrl,
        releasePid: streamingData?.releasePid || movieVideo.releasePid,
        releaseUrl: streamingData?.releaseUrl || movieVideo.releaseUrl,
        duration: streamingData?.duration || movieVideo.duration,
        dash_widevine_ads: streamingData || null,
        dPlay: !isLowEndLG() ? noDrmConfig?.dPlay || null : null,
        isEnableNoDrm: !isLowEndLG()
          ? isEnableNoDrm(this.state.adsCountryRights, noDrmConfig, movieVideo) || false
          : false,
        adsCountryRights: this.state.adsCountryRights || [],
        filmstrip: currentHas4K ? undefined : this.mediaData.mediaHD.content.filmstrip,
        title: this.state.title,
        description: this.state.description,
        playbackTime: this.isRestarting ? null : getProgressData.call(this),
        id: this.state.id,
        has4K: this.state.has4K,
        externalSubtitles: currentHas4K
          ? this.mediaData.media4K.content.subtitles
          : this.mediaData.mediaHD.content.subtitles,
        mediaGuid: currentHas4K ? this.mediaData.media4K.mediaGuid : this.mediaData.mediaHD.mediaGuid,
        genre: this.state.titleData.genres[0],
        addonContent: this.state.addonContent,

        contentType: isFVODFreeToAir(this.state.titleData?.tags) ? "FVOD" : this.state.titleData.priorityType,
        profileType: profileType,
        contentOwnership,
        isLive: this.state.isLive,
        genres: this.state.titleData.genres,
        assetTypes: this.state.titleData.assetTypes,
        backgroundCover: this.state.backgroundCover,
        isEnableVideoCommerce: this.state.titleData.ecommerceAds || false,
        content_language: this.state.titleData.languagesCode[0],
        content_rating: this.state.titleData.ageRating,
        device_model: getModelDevice,
        enforceAds: noDrmConfig?.enforceAds || false
      }
    : {
        videoType: "series",
        contentTypeYoubora: getContentTypeForAnalytics(this.state),
        manifest: streamingData?.streamingUrl || movieVideo.streamingUrl,
        releasePid: streamingData?.releasePid || movieVideo.releasePid,
        releaseUrl: streamingData?.releaseUrl || movieVideo.releaseUrl,
        duration: streamingData?.duration || movieVideo.duration,
        dash_widevine_ads: streamingData || null,
        filmstrip: await getEpisodeFilmstripData.call(this, currentHas4K),
        adsCountryRights:
          (await getAdsCountryRightsData.call(this, episode.tvSeasonNumber, episode.tvSeasonEpisodeNumber)) || [],
        description: checkEpisodeData.call(this, SHORT_DESCRIPTION),
        title: checkEpisodeData.call(this, TITLE),
        dPlay: !isLowEndLG() ? noDrmConfig?.dPlay || null : null,
        isEnableNoDrm: !isLowEndLG()
          ? isEnableNoDrm(this.state.adsCountryRights, noDrmConfig, movieVideo) || false
          : false,
        tvSeasonNumber: checkEpisodeData.call(this, SEASON_NUMBER),
        tvSeasonEpisodeNumber: checkEpisodeData.call(this, EPISODE_NUMBER),
        playbackTime: this.isRestarting ? null : getProgressData.call(this),
        seriesTitle: this.state.title,
        episodeLabel: getEpisodeLabel(episode.tvSeasonNumber, episode.tvSeasonEpisodeNumber),
        seasonsList: this.seasonsList,
        episodesList: this.episodesList,
        id: checkEpisodeData.call(this, ID),
        seriesId: this.state.id,
        has4K: this.state.has4K,
        //externalSubtitles: currentHas4K && this.state.externalSubtitles
        externalSubtitles: await getEpisodeExternalSubtitles.call(this, currentHas4K),
        mediaGuid: currentHas4K ? this.mediaData.media4K.mediaGuid : this.mediaData.mediaHD.mediaGuid,
        credits: this.episodesList[
          findEpisodeIndex(this.episodesList, episode.tvSeasonNumber, episode.tvSeasonEpisodeNumber)
        ].mediaContent[0].credits,
        genre: this.state.titleData.genres[0],
        addonContent: this.state.addonContent,

        contentType: isFVODFreeToAir(episode?.tags) ? "FVOD" : this.state.titleData.priorityType,
        contentOwnership: (this.state.titleData?.contentOwnership && this.state.titleData?.contentOwnership[0]) || "",
        profileType: profileType,
        isLive: this.state.isLive,
        genres: this.state.titleData.genres,
        assetTypes: this.state.titleData.assetTypes,
        backgroundCover: this.state.backgroundCover,
        isEnableVideoCommerce: episode?.ecommerceAds || false,
        content_language: this.state.titleData.languagesCode[0],
        content_rating: this.state.titleData.ageRating,
        device_model: getModelDevice,
        enforceAds: noDrmConfig?.enforceAds || false
      };
}

/**
 * Gets media data object depending on whether it is a series and it has been started watching (in this case
 * it will check last seen episode data) or a movie/not started series. Needs a React
 * component class context since this method is making use of inner context properties.
 * @param {Object} title - Title data object
 * @param {Boolean} is4K - The title should be 4k
 * @returns {Object} Title media content object.
 * @public
 */
export function getMediaContentData(title, is4K) {
  if (this.lastSeenEpisode) {
    // Is resumed playback if series
    const lastSeenEpisode = !is4K ? this.lastSeenEpisode : this.lastSeenEpisode.title4K;
    return lastSeenEpisode && lastSeenEpisode.mediaContent[0];
  }
  // Is fresh start playback or movie
  // If 4k series check whether the first available episode has 4k media.
  const firstEpisode = this.isSeries && `S${padStart(this.seasonsListRaw[0].tvSeasonNumber, 2)}E001`;
  const firstEpisodeFromMediaGuid =
    this.isSeries &&
    title.mediaContent.find(content => getSeasonEpisodeFromMediaGuid(content.mediaGuid) === firstEpisode);
  return !is4K || !this.isSeries
    ? title.mediaContent[0]
    : firstEpisodeFromMediaGuid
    ? firstEpisodeFromMediaGuid
    : undefined;
}

export function isEnableNoDrm(adsCountryRights, noDrmConfig, mediaObj) {
  const adCountry = getAppState().country.toUpperCase();
  const isSVODUser = getAppState()?.isSvodUser;
  const enforceAds = noDrmConfig?.enforceAds || false;
  const isShowAds = !isSVODUser ? true : enforceAds && isSVODUser ? true : false;
  const isAdsExist =
    isShowAds && adsCountryRights && adsCountryRights.length > 0 && adsCountryRights.indexOf(adCountry) > -1;
  if (isAdsExist) {
    return !!(
      (noDrmConfig?.dPlay === DPLAY.free && mediaObj.dash_widevine_fp_ads) ||
      (noDrmConfig?.dPlay === DPLAY.token && mediaObj.dash_widevine_tk_ads)
    );
  } else {
    return !!(
      (noDrmConfig?.dPlay === DPLAY.free && mediaObj.dash_widevine_fp) ||
      (noDrmConfig?.dPlay === DPLAY.token && mediaObj.dash_widevine_tk)
    );
  }
}

export function getStreamingURL(adsCountryRights, isEnableVideoCommerce, onDrmConfig, mediaObj) {
  let streamingData = null;
  const adCountry = getAppState().country.toUpperCase();
  const isSVODUser = getAppState()?.isSvodUser;
  const enforceAds = onDrmConfig?.enforceAds || false;
  const isShowAds = !isSVODUser ? true : enforceAds && isSVODUser ? true : false;
  const isAdsExist =
    isShowAds && adsCountryRights && adsCountryRights.length > 0 && adsCountryRights.indexOf(adCountry) > -1;
  if (isAdsExist && isEnableVideoCommerce !== "true") {
    if (onDrmConfig?.dPlay === DPLAY.free) {
      streamingData = !isLowEndLG()
        ? mediaObj.dash_widevine_fp_ads || mediaObj.dash_widevine_ads || mediaObj.dash_widevine
        : mediaObj.dash_widevine;
    } else if (onDrmConfig?.dPlay === DPLAY.token) {
      streamingData = !isLowEndLG()
        ? mediaObj.dash_widevine_tk_ads || mediaObj.dash_widevine_ads || mediaObj.dash_widevine
        : mediaObj.dash_widevine;
    } else {
      streamingData = !isLowEndLG() ? mediaObj.dash_widevine_ads || mediaObj.dash_widevine : mediaObj.dash_widevine;
    }
  } else {
    if (onDrmConfig?.dPlay === DPLAY.free) {
      streamingData = !isLowEndLG() ? mediaObj.dash_widevine_fp || mediaObj.dash_widevine : mediaObj.dash_widevine;
    } else if (onDrmConfig?.dPlay === DPLAY.token) {
      streamingData = !isLowEndLG() ? mediaObj.dash_widevine_tk || mediaObj.dash_widevine : mediaObj.dash_widevine;
    } else {
      streamingData = mediaObj.dash_widevine;
    }
  }
  return streamingData;
}
